                                                                                                                                                                                    import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import showToast from '../../components/Toast';
import config from '../../config/frontend.config';
import { resetLogoutFlag } from '../../AxiosInstance';

const GoogleCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasProcessed, setHasProcessed] = useState(false);
  const toastShownRef = useRef(false);
  const apiCallMadeRef = useRef(false);

  useEffect(() => {
    if (hasProcessed) return; // Prevent multiple executions
    
    const token = searchParams.get('token');
    const userId = searchParams.get('userId');
    const error = searchParams.get('error');

    if (error) {
      setError('Google authentication failed. Please try again.');
      setLoading(false);
      setHasProcessed(true);
      showToast.error('Google authentication failed. Please try again.');
      setTimeout(() => {
        navigate('/auth');
      }, 3000);
      return;
    }

    if (token && userId) {
      setHasProcessed(true);
      handleGoogleAuthSuccess(token, userId);
    } else {
      setError('Invalid authentication response from Google.');
      setLoading(false);
      setHasProcessed(true);
      showToast.error('Invalid authentication response from Google.');
      setTimeout(() => {
        navigate('/auth');
      }, 3000);
    }
  }, [searchParams, navigate, hasProcessed]);

  const handleGoogleAuthSuccess = async (token, userId) => {
    try {
      // Reset logout flag on successful login
      resetLogoutFlag();
      
      // Store token and user data
      config.setAuthToken(token);
      config.setUserId(userId);
      
      // Fetch user details to get role information using the same pattern as normal login
      try {
        // Prevent multiple API calls
        if (apiCallMadeRef.current) {
          return;
        }
        apiCallMadeRef.current = true;
        
        const response = await fetch(`${config.api.baseURL}/${config.api.apiPath}/user/userdata?UserId=${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'id': userId,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const userData = await response.json();
          if (userData.statusCode === 200) {
            const user = userData.data;
            
            // Store role information - normalize role name to match frontend expectations
            const normalizedRole = user.Role === 'Admin' ? 'Admin' : (user.Role || 'Member');
            config.setUserRole(normalizedRole);
            config.setUserData(user);
            
            // Check if this is a new Google user who needs to set company name or password
            const needsCompanyName = !user.CompanyName || user.CompanyName.trim() === '';
            const isNewGoogleUser = user.isGoogleUser && (!user.Password || user.Password === '');
            
            // Show success message (prevent duplicate in React.StrictMode)
            if (!toastShownRef.current) {
              if (needsCompanyName) {
                showToast.success('Google login successful! Please set your company name to complete setup.');
              } else if (isNewGoogleUser) {
                showToast.success('Google login successful! Please set your password to complete setup.');
              } else {
                showToast.success('Google login successful!');
              }
              toastShownRef.current = true;
            }
            
            // Dispatch custom event to notify App component
            window.dispatchEvent(new Event('loginSuccess'));
            
            // Navigate to appropriate page - Company name first, then password
            setTimeout(() => {
              if (needsCompanyName) {
                navigate('/auth/company-setup');
              } else if (isNewGoogleUser) {
                navigate('/auth/password-setup');
              } else {
                navigate('/');
              }
            }, 100);
          } else {
            console.error('Failed to fetch user data:', userData);
            throw new Error('Failed to fetch user data');
          }
        } else {
          console.error('Failed to fetch user data, status:', response.status);
          throw new Error('Failed to fetch user data');
        }
      } catch (fetchError) {
        console.error('Error fetching user data:', fetchError);
        // Still proceed with login using token data
        window.dispatchEvent(new Event('loginSuccess'));
        setTimeout(() => {
          navigate('/');
        }, 100);
      }
    } catch (error) {
      console.error('Google auth callback error:', error);
      setError('Failed to complete Google authentication.');
      showToast.error('Failed to complete Google authentication.');
      setTimeout(() => {
        navigate('/auth');
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '3px solid rgba(255,255,255,0.3)',
          borderTop: '3px solid white',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <h2 style={{ marginTop: '20px', marginBottom: '10px' }}>Completing Google Login...</h2>
        <p style={{ opacity: 0.8 }}>Please wait while we set up your account.</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        textAlign: 'center',
        padding: '20px'
      }}>
        <div style={{
          fontSize: '4rem',
          marginBottom: '20px'
        }}>⚠️</div>
        <h2 style={{ marginBottom: '10px' }}>Authentication Error</h2>
        <p style={{ opacity: 0.8, marginBottom: '20px' }}>{error}</p>
        <p style={{ opacity: 0.6, fontSize: '0.9rem' }}>Redirecting to login page...</p>
      </div>
    );
  }

  return null;
};

export default GoogleCallback;
 